import { useEffect, useRef, type CSSProperties } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DesktopShell } from '../components/DesktopShell';
import { TopBar } from '../components/TopBar';
import { Card } from '../components/Card';
import { Btn } from '../components/Btn';
import { Flag } from '../components/Flag';
import { Seg } from '../components/Seg';
import { Ic } from '../components/Icon';
import { MatchCardLg } from '../components/MatchCardLg';
import { matchesApi } from '../api/matches';
import { betsApi } from '../api/bets';
import { teamNamePl } from '../lib/flags';
import { phaseLabel, PHASE_LABEL } from '../lib/phase';
import { formatDateOnly, formatDayName, formatTime, formatYmd } from '../lib/date';
import { pickDisplayScore } from '../lib/score';
import type { Match, MatchPhase } from '../api/types';

type View = 'cards' | 'timeline' | 'table';
type PhaseFilter = 'all' | MatchPhase;
type GroupFilter = 'all' | string;

const PHASES: { v: PhaseFilter; l: string }[] = [
  { v: 'all', l: 'Wszystkie' },
  { v: 'GROUP', l: 'Faza grupowa' },
  { v: 'ROUND_OF_32', l: '1/16' },
  { v: 'ROUND_OF_16', l: '1/8' },
  { v: 'QUARTER_FINAL', l: 'Ćwierćfinały' },
  { v: 'SEMI_FINAL', l: 'Półfinały' },
  { v: 'THIRD_PLACE', l: '3. miejsce' },
  { v: 'FINAL', l: 'Finał' },
];

const GROUPS: { v: GroupFilter; l: string }[] = [
  { v: 'all', l: 'Wszystkie' },
  ...['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'].map((g) => ({
    v: g as GroupFilter,
    l: `Grupa ${g}`,
  })),
];

const pillStyle = (active: boolean): CSSProperties => ({
  height: 34,
  padding: '0 14px',
  borderRadius: 17,
  border: '1px solid',
  borderColor: active ? '#0f1e3d' : 'rgba(15,30,61,.12)',
  background: active ? '#0f1e3d' : '#fff',
  color: active ? '#fff' : '#0f1e3d',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
});

const PHASE_VALUES: PhaseFilter[] = [
  'all',
  'GROUP',
  'ROUND_OF_32',
  'ROUND_OF_16',
  'QUARTER_FINAL',
  'SEMI_FINAL',
  'THIRD_PLACE',
  'FINAL',
];
const VIEW_VALUES: View[] = ['cards', 'timeline', 'table'];
const GROUP_VALUES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];

export function CalendarPage() {
  const location = useLocation();
  const [params, setParams] = useSearchParams();

  const phaseParam = params.get('phase');
  const phase: PhaseFilter = (PHASE_VALUES as string[]).includes(phaseParam ?? '')
    ? (phaseParam as PhaseFilter)
    : 'all';
  const groupParam = params.get('group');
  const group: GroupFilter = phase === 'GROUP' && groupParam && GROUP_VALUES.includes(groupParam)
    ? groupParam
    : 'all';
  const viewParam = params.get('view');
  const view: View = (VIEW_VALUES as string[]).includes(viewParam ?? '')
    ? (viewParam as View)
    : 'cards';
  const focusMatchId = params.get('match');

  const updateParams = (mut: (p: URLSearchParams) => void) => {
    const next = new URLSearchParams(params);
    mut(next);
    setParams(next, { replace: true });
  };
  const setPhase = (p: PhaseFilter) =>
    updateParams((u) => {
      if (p === 'all') u.delete('phase');
      else u.set('phase', p);
      if (p !== 'GROUP') u.delete('group');
      u.delete('match');
    });
  const setGroup = (g: GroupFilter) =>
    updateParams((u) => {
      if (g === 'all') u.delete('group');
      else u.set('group', g);
      u.delete('match');
    });
  const setView = (v: View) =>
    updateParams((u) => {
      if (v === 'cards') u.delete('view');
      else u.set('view', v);
    });

  const filters = phase === 'GROUP'
    ? { phase: 'GROUP' as MatchPhase, ...(group !== 'all' && { group }) }
    : phase === 'all'
      ? undefined
      : { phase };

  const matches = useQuery({
    queryKey: ['matches', phase, phase === 'GROUP' ? group : 'all'],
    queryFn: () => matchesApi.list(filters),
  });
  const myBets = useQuery({ queryKey: ['my-bets'], queryFn: betsApi.mine });

  const betByMatch: Record<string, { home: number; away: number; points: number | null }> = {};
  (myBets.data ?? []).forEach((b) => {
    betByMatch[b.matchId] = { home: b.homeScore, away: b.awayScore, points: b.points };
  });

  useEffect(() => {
    if (!focusMatchId || !matches.data) return;
    const el = document.getElementById(`match-${focusMatchId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    updateParams((u) => u.delete('match'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMatchId, matches.data]);

  const scrolledToTodayRef = useRef(false);
  useEffect(() => {
    if (scrolledToTodayRef.current) return;
    if (focusMatchId) return;
    if (view !== 'cards') return;
    if (!matches.data || matches.data.length === 0) return;
    scrolledToTodayRef.current = true;
    const ymds = Array.from(new Set(matches.data.map((m) => formatYmd(m.kickoff)))).sort();
    const today = formatYmd(new Date().toISOString());
    const target =
      ymds.find((d) => d === today) ?? ymds.find((d) => d > today) ?? ymds[ymds.length - 1];
    const el = document.getElementById(`date-${target}`);
    if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
  }, [view, focusMatchId, matches.data]);

  const fromUrl = `/calendar${location.search}`;

  return (
    <DesktopShell>
      <TopBar
        title="Kalendarz mistrzostw"
        subtitle="2026 · USA · Kanada · Meksyk · 11 czerwca – 19 lipca"
      />
      <div style={{ flex: 1, padding: '22px 36px 36px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
            {PHASES.map((p) => (
              <button
                key={p.v}
                onClick={() => setPhase(p.v)}
                style={pillStyle(phase === p.v)}
              >
                {p.l}
              </button>
            ))}
          </div>

          {phase === 'GROUP' && (
            <div
              className="calendar-group-filter"
              style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}
            >
              {GROUPS.map((g) => (
                <button
                  key={g.v}
                  onClick={() => setGroup(g.v)}
                  className={g.v === 'all' ? 'calendar-group-filter-all' : 'calendar-group-filter-item'}
                  style={pillStyle(group === g.v)}
                >
                  {g.l}
                </button>
              ))}
            </div>
          )}

          <Seg
            options={[
              { value: 'cards', label: 'Karty', icon: <Ic.grid size={14} /> },
              { value: 'timeline', label: 'Oś czasu', icon: <Ic.timeline size={14} /> },
              { value: 'table', label: 'Tabela', icon: <Ic.rows size={14} /> },
            ]}
            value={view}
            onChange={setView}
          />
        </div>

        {matches.isLoading && <Card padding={24}>Ładowanie meczów…</Card>}

        {matches.data && matches.data.length === 0 && (
          <Card padding={24} style={{ color: '#5b6578', textAlign: 'center' }}>
            Brak meczów dla wybranej fazy.
          </Card>
        )}

        {matches.data && matches.data.length > 0 && (
          <>
            {view === 'cards' && <CardsView matches={matches.data} bets={betByMatch} fromUrl={fromUrl} />}
            {view === 'timeline' && <TimelineView matches={matches.data} bets={betByMatch} fromUrl={fromUrl} />}
            {view === 'table' && <TableView matches={matches.data} bets={betByMatch} fromUrl={fromUrl} />}
          </>
        )}
      </div>
    </DesktopShell>
  );
}

interface ViewProps {
  matches: Match[];
  bets: Record<string, { home: number; away: number; points: number | null }>;
  fromUrl: string;
}

function CardsView({ matches, bets, fromUrl }: ViewProps) {
  const byDate: Record<string, Match[]> = {};
  matches.forEach((m) => {
    const d = formatYmd(m.kickoff);
    (byDate[d] = byDate[d] || []).push(m);
  });
  const dates = Object.keys(byDate).sort();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
      {dates.map((date) => (
        <div key={date} id={`date-${date}`} style={{ scrollMarginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
            <div className="serif" style={{ fontSize: 22, fontWeight: 700 }}>
              {formatDateOnly(byDate[date][0].kickoff)}
            </div>
            <div style={{ fontSize: 12, color: '#5b6578' }}>
              {formatDayName(byDate[date][0].kickoff)}
            </div>
            <div style={{ flex: 1, height: 1, background: 'rgba(15,30,61,.08)' }} />
          </div>
          <div
            className="calendar-cards"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: 14,
              maxWidth: 1108,
            }}
          >
            {byDate[date].map((m) => (
              <MatchCardLg key={m.id} match={m} myBet={bets[m.id]} fromUrl={fromUrl} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TimelineView({ matches, bets, fromUrl }: ViewProps) {
  const byDate: Record<string, Match[]> = {};
  matches.forEach((m) => {
    const d = formatYmd(m.kickoff);
    (byDate[d] = byDate[d] || []).push(m);
  });
  const dates = Object.keys(byDate).sort();
  return (
    <div className="calendar-timeline" style={{ position: 'relative', paddingLeft: 132 }}>
      <div
        className="calendar-timeline-rail"
        style={{
          position: 'absolute',
          left: 100,
          top: 16,
          bottom: 16,
          width: 2,
          background: 'rgba(15,30,61,.08)',
        }}
      />
      {dates.map((date) => (
        <div key={date} style={{ position: 'relative', marginBottom: 18 }}>
          <div className="calendar-timeline-date-mobile" style={{ display: 'none' }}>
            <div className="serif" style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
              {formatDateOnly(byDate[date][0].kickoff)} · {formatDayName(byDate[date][0].kickoff)}
            </div>
          </div>
          <div className="calendar-timeline-date-side" style={{ position: 'absolute', left: -132, top: 0, width: 80, textAlign: 'right' }}>
            <div className="serif" style={{ fontSize: 28, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.02em' }}>
              {new Date(byDate[date][0].kickoff).getDate()}
            </div>
            <div style={{ fontSize: 11, color: '#5b6578', marginTop: 4 }}>
              {formatDateOnly(byDate[date][0].kickoff).split(' ')[1]} · {formatDayName(byDate[date][0].kickoff)}
            </div>
          </div>
          <div
            className="calendar-timeline-dot"
            style={{
              position: 'absolute',
              left: -38,
              top: 6,
              width: 14,
              height: 14,
              borderRadius: 7,
              background: '#fff',
              border: '3px solid var(--accent)',
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {byDate[date].map((m) => {
              const done = m.status === 'FINISHED';
              const live = m.status === 'LIVE';
              const myBet = bets[m.id];
              return (
                <Link
                  key={m.id}
                  id={`match-${m.id}`}
                  to={`/bet/${m.id}`}
                  state={{ from: fromUrl }}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                <Card
                  className="calendar-timeline-card"
                  padding={12}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '60px minmax(0,1fr) 24px 76px 24px minmax(0,1fr) 200px 80px',
                    alignItems: 'center',
                    gap: 10,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontSize: 12, color: '#5b6578', fontWeight: 600 }}>
                    {formatTime(m.kickoff)}
                    {live && <div style={{ color: '#d0463c', fontSize: 10 }}>LIVE</div>}
                  </div>
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: 13.5,
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {m.homePlaceholder ?? teamNamePl(m.homeTeam)}
                  </span>
                  <Flag code={m.homePlaceholder ?? m.homeTeam} size={24} />
                  {(() => {
                    const ds = (done || live) ? pickDisplayScore(m) : null;
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span
                          className="num"
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: live ? '#d0463c' : done ? '#0f1e3d' : 'rgba(15,30,61,.3)',
                            textAlign: 'center',
                          }}
                        >
                          {ds ? `${ds.mainHome} – ${ds.mainAway}` : '– : –'}
                        </span>
                        {ds?.annotationShort && (
                          <span style={{ fontSize: 10, color: '#5b6578', marginTop: 1, whiteSpace: 'nowrap' }}>
                            {ds.annotationShort}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  <Flag code={m.awayPlaceholder ?? m.awayTeam} size={24} />
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: 13.5,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {m.awayPlaceholder ?? teamNamePl(m.awayTeam)}
                  </span>
                  <div className="calendar-timeline-meta" style={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'right', minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: '#5b6578' }}>{phaseLabel(m.phase, m.group)}</div>
                    {m.venue && (
                      <div
                        style={{
                          fontSize: 10.5,
                          color: '#5b6578',
                          opacity: 0.75,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={m.venue}
                      >
                        {m.venue}
                      </div>
                    )}
                  </div>
                  <div
                    className="calendar-timeline-bet"
                    style={{ display: 'flex', justifyContent: 'flex-end' }}
                  >
                    {myBet ? (
                      <div
                        className="mono"
                        style={{
                          width: '100%',
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 13,
                          fontWeight: 700,
                          borderRadius: 8,
                          background: 'rgba(15,30,61,.05)',
                        }}
                      >
                        {myBet.home}–{myBet.away}
                      </div>
                    ) : m.status === 'UPCOMING' ? (
                      <Btn size="sm" variant="accent" fullWidth>
                        Obstaw
                      </Btn>
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#5b6578',
                          fontSize: 12,
                        }}
                      >
                        —
                      </div>
                    )}
                  </div>
                </Card>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function TableView({ matches, bets, fromUrl }: ViewProps) {
  return (
    <Card padding={0} className="calendar-table">
      <div
        className="calendar-table-row calendar-table-head"
        style={{
          display: 'grid',
          gridTemplateColumns: '80px 70px 1.2fr 90px 1.2fr 95px 1.4fr 75px 65px',
          padding: '14px 20px',
          fontSize: 10.5,
          color: '#5b6578',
          fontWeight: 600,
          letterSpacing: '.06em',
          borderBottom: '1px solid rgba(15,30,61,.08)',
        }}
      >
        <div>DATA</div>
        <div>GODZ</div>
        <div style={{ textAlign: 'right' }}>GOSPODARZE</div>
        <div style={{ textAlign: 'center' }}>WYNIK</div>
        <div>GOŚCIE</div>
        <div>FAZA</div>
        <div>STADION</div>
        <div style={{ textAlign: 'right' }}>TYP</div>
        <div style={{ textAlign: 'right' }}>PKT</div>
      </div>
      {matches.map((m, i) => {
        const done = m.status === 'FINISHED';
        const live = m.status === 'LIVE';
        const myBet = bets[m.id];
        return (
          <Link
            key={m.id}
            id={`match-${m.id}`}
            to={`/bet/${m.id}`}
            state={{ from: fromUrl }}
            className="calendar-table-row"
            style={{
              display: 'grid',
              gridTemplateColumns: '80px 70px 1.2fr 90px 1.2fr 95px 1.4fr 75px 65px',
              padding: '12px 20px',
              fontSize: 13.5,
              alignItems: 'center',
              borderBottom: i < matches.length - 1 ? '1px solid rgba(15,30,61,.05)' : 'none',
              cursor: 'pointer',
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            <div className="num" style={{ fontSize: 12, color: '#5b6578' }}>
              {formatDateOnly(m.kickoff)}
            </div>
            <div className="num" style={{ fontWeight: 600 }}>
              {formatTime(m.kickoff)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
              <span style={{ fontWeight: 500 }} className="desktop-only">{m.homePlaceholder ?? teamNamePl(m.homeTeam)}</span>
              <span style={{ fontWeight: 500 }} className="mobile-only">{m.homePlaceholder ?? m.homeTeam}</span>
              <Flag code={m.homePlaceholder ?? m.homeTeam} size={22} />
            </div>
            <div style={{ textAlign: 'center' }}>
              {(() => {
                const ds = (done || live) ? pickDisplayScore(m) : null;
                if (!ds) return <span style={{ fontSize: 12, color: '#5b6578' }}>–</span>;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span
                      className="num serif"
                      style={{ fontSize: 17, fontWeight: 700, color: live ? '#d0463c' : '#0f1e3d' }}
                    >
                      {ds.mainHome}–{ds.mainAway}
                    </span>
                    {ds.annotationShort && (
                      <span style={{ fontSize: 10, color: '#5b6578', marginTop: 1, whiteSpace: 'nowrap' }}>
                        {ds.annotationShort}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Flag code={m.awayPlaceholder ?? m.awayTeam} size={22} />
              <span style={{ fontWeight: 500 }} className="desktop-only">{m.awayPlaceholder ?? teamNamePl(m.awayTeam)}</span>
              <span style={{ fontWeight: 500 }} className="mobile-only">{m.awayPlaceholder ?? m.awayTeam}</span>
            </div>
            <div style={{ fontSize: 11.5, color: '#5b6578' }}>{PHASE_LABEL[m.phase]}</div>
            <div
              style={{
                fontSize: 11.5,
                color: '#5b6578',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={m.venue ?? undefined}
            >
              {m.venue ?? '—'}
            </div>
            <div style={{ textAlign: 'right' }}>
              {myBet ? (
                <span className="mono" style={{ fontSize: 12, fontWeight: 700 }}>
                  {myBet.home}–{myBet.away}
                </span>
              ) : (
                <span style={{ fontSize: 12, color: '#5b6578' }}>—</span>
              )}
            </div>
            <div
              className="num serif"
              style={{
                textAlign: 'right',
                fontWeight: 700,
                color: done && myBet ? ((myBet.points ?? 0) > 0 ? '#0f6b34' : '#8a2a22') : '#5b6578',
              }}
            >
              {done && myBet ? myBet.points ?? 0 : live ? '…' : '—'}
            </div>
          </Link>
        );
      })}
    </Card>
  );
}
